import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, Badge, statusBadge } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeftIcon, TruckIcon, WrenchIcon, CalendarCheckIcon, TrashIcon, ChatCircleDotsIcon, MapPinIcon, NavigationArrowIcon, LinkIcon, ArrowsClockwiseIcon, GasPumpIcon, PencilSimpleIcon, CheckIcon, XIcon } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import MotiveLinkDialog from "@/components/MotiveLinkDialog";
import NearbyServiceDialog from "@/components/NearbyServiceDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { MapPinLineIcon, LockSimpleIcon, ShareNetworkIcon, CopyIcon } from "@phosphor-icons/react";
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import L from "leaflet";

const pinDropIcon = new L.DivIcon({
  className: "pin-drop",
  html: '<div style="background:#059669;width:26px;height:26px;border-radius:13px 13px 13px 0;transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;color:white;border:3px solid white;box-shadow:0 3px 8px rgba(0,0,0,0.35)"><span style="transform:rotate(45deg);font-size:12px">🚛</span></div>',
  iconSize: [26, 26],
  iconAnchor: [13, 26],
});

function ClickToPin({ point, onPick }) {
  useMapEvents({ click: (e) => onPick({ lat: e.latlng.lat, lng: e.latlng.lng }) });
  return point ? <Marker position={[point.lat, point.lng]} icon={pinDropIcon} /> : null;
}
import FuelGauge from "@/components/FuelGauge";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine } from "recharts";
import { detectRegion, formatVolume, formatEconomy, formatDistance, unitSystemFromTruck } from "@/lib/units";

export default function TruckDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [truck, setTruck] = useState(null);
  const [repairs, setRepairs] = useState([]);
  const [services, setServices] = useState([]);
  const [driver, setDriver] = useState(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [nearbyOpen, setNearbyOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [fuelHistory, setFuelHistory] = useState([]);
  const [fuelEvents, setFuelEvents] = useState([]);
  const [chartRange, setChartRange] = useState("month"); // day | week | month
  // YYYY-MM-DD (LOCAL time); anchors day view (start) and week view (end-of-week)
  const [chartDate, setChartDate] = useState(() => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  });
  const [editingTank, setEditingTank] = useState(false);
  const [tankVal, setTankVal] = useState("");
  const [holdOpen, setHoldOpen] = useState(false);
  const [holdAddress, setHoldAddress] = useState("");
  const [holding, setHolding] = useState(false);
  const [pinPoint, setPinPoint] = useState(null); // {lat,lng} chosen on the map
  const [pinCity, setPinCity] = useState("");      // city name resolved in-browser
  const [shareOpen, setShareOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [shareExpires, setShareExpires] = useState(null);
  const [creatingShare, setCreatingShare] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [shareDays, setShareDays] = useState(7);

  const load = async () => {
    const [t, r, s, drivers] = await Promise.all([
      api.get(`/trucks/${id}`),
      api.get(`/repairs?truck_id=${id}`),
      api.get(`/services?truck_id=${id}`),
      api.get(`/drivers`),
    ]);
    setTruck(t.data);
    setRepairs(r.data);
    setServices(s.data);
    setDriver(drivers.data.find((d) => d.id === t.data.driver_id) || null);
    setTankVal(t.data.fuel_tank_capacity_gallons ?? "");
    // Fuel telemetry (only when linked to Motive — silently ignore errors)
    if (t.data.motive_vehicle_id) {
      try {
        const fe = await api.get(`/trucks/${id}/fuel-events?limit=10`);
        setFuelEvents(fe.data || []);
      } catch { /* ignore */ }
    }
  };

  // Fuel-history loader keyed off the chart filter, kept separate so flipping
  // range/date doesn't re-fetch repairs/services.
  const loadFuelHistory = async () => {
    if (!truck?.motive_vehicle_id) return;
    try {
      let url;
      if (chartRange === "day") {
        // Full 24 h of the picked day in the user's LOCAL timezone.
        const [y, m, d] = chartDate.split("-").map(Number);
        const start = new Date(y, m - 1, d, 0, 0, 0, 0);
        const end = new Date(y, m - 1, d + 1, 0, 0, 0, 0);
        url = `/trucks/${id}/fuel-history?from_date=${encodeURIComponent(start.toISOString())}&to_date=${encodeURIComponent(end.toISOString())}`;
      } else if (chartRange === "week") {
        // 7 days ending on chartDate (inclusive), local timezone.
        const [y, m, d] = chartDate.split("-").map(Number);
        const start = new Date(y, m - 1, d - 6, 0, 0, 0, 0);
        const end = new Date(y, m - 1, d + 1, 0, 0, 0, 0);
        url = `/trucks/${id}/fuel-history?from_date=${encodeURIComponent(start.toISOString())}&to_date=${encodeURIComponent(end.toISOString())}`;
      } else {
        url = `/trucks/${id}/fuel-history?days=30`;
      }
      const { data } = await api.get(url);
      setFuelHistory(data || []);
    } catch { /* ignore */ }
  };

  useEffect(() => { load(); }, [id]);
  useEffect(() => { loadFuelHistory(); /* eslint-disable-next-line */ }, [id, chartRange, chartDate, truck?.motive_vehicle_id]);

  const del = async () => {
    if (!window.confirm("Delete this truck and all its history?")) return;
    await api.delete(`/trucks/${id}`);
    toast.success("Truck deleted");
    nav("/trucks");
  };

  const refreshLocation = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/integrations/motive/refresh-locations");
      if (data.ok) toast.success(`Locations updated`);
      else toast.error(`Refresh failed: ${data.reason}`);
      await load();
    } catch (e) {
      toast.error("Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  const resolvePinCity = async (lat, lng) => {
    setPinCity("");
    try {
      const r = await fetch(`https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lng}&localityLanguage=en`);
      const d = await r.json();
      const city = d.city || d.locality;
      const region = d.principalSubdivision;
      const parts = [city, region].filter(Boolean).filter((v, i, a) => a.indexOf(v) === i);
      setPinCity(parts.join(", ") || d.countryName || "");
    } catch { /* leave blank — backend will fall back to coords */ }
  };

  const onPickPin = (pt) => { setPinPoint(pt); resolvePinCity(pt.lat, pt.lng); };

  const holdLocation = async () => {
    const hasPin = pinPoint && pinPoint.lat != null;
    if (!hasPin && !holdAddress.trim()) { toast.error("Click the map to drop a pin, or enter the parked address"); return; }
    setHolding(true);
    try {
      const payload = hasPin
        ? { latitude: pinPoint.lat, longitude: pinPoint.lng, location_address: pinCity || undefined }
        : { address: holdAddress.trim() };
      await api.post(`/trucks/${id}/hold-location`, payload);
      toast.success("Location pinned & held. Motive won't move this truck until you release it.");
      setHoldOpen(false);
      setHoldAddress("");
      setPinPoint(null);
      setPinCity("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not hold location");
    } finally {
      setHolding(false);
    }
  };

  const releaseLocation = async () => {
    try {
      await api.post(`/trucks/${id}/release-location`);
      toast.success("Location released — live tracking resumed");
      await load();
    } catch (e) {
      toast.error("Could not release location");
    }
  };

  const createShare = async (days) => {
    setCreatingShare(true);
    try {
      const { data } = await api.post(`/trucks/${id}/share-link`, { days });
      const base = process.env.REACT_APP_PUBLIC_BASE_URL || window.location.origin;
      const slug = encodeURIComponent(truck.unit_number || "unit");
      setShareUrl(`${base}/track/${slug}/${data.token}`);
      setShareExpires(data.expires_at ? new Date(data.expires_at) : null);
      setShareDays(days);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create link");
      setShareOpen(false);
    } finally {
      setCreatingShare(false);
    }
  };

  const openShare = async () => {
    setShareOpen(true);
    await createShare(shareDays);
  };

  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 1800);
      toast.success("Link copied");
    } catch { /* ignore */ }
  };

  const revokeShare = async () => {
    try {
      await api.post(`/trucks/${id}/share-link/revoke`);
      toast.success("Link turned off — it will no longer work");
      setShareOpen(false);
      setShareUrl("");
      await load();
    } catch (e) {
      toast.error("Could not revoke link");
    }
  };

  const saveTank = async () => {    try {
      const gal = tankVal === "" ? 0 : Number(tankVal);
      await api.patch(`/trucks/${id}/fuel-tank`, { fuel_tank_capacity_gallons: gal });
      toast.success(gal ? `Tank set to ${gal} gal — MPG & fill-up gallons will populate on the next fill` : "Tank capacity cleared");
      setEditingTank(false);
      await load();
    } catch (e) {
      toast.error("Failed to save tank size");
    }
  };

  if (!truck) return <div className="p-12 font-mono text-sm text-slate-500">LOADING...</div>;

  const sb = statusBadge(truck.status);
  const total_repair_cost = repairs.reduce((sum, r) => sum + Number(r.total_cost || 0), 0);

  return (
    <div data-testid="truck-detail-page">
      <PageHeader
        eyebrow={`Unit ${truck.unit_number}`}
        title={`${truck.year} ${truck.make} ${truck.model}`}
        subtitle={`VIN: ${truck.vin}`}
      >
        <Link to="/trucks" className="text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1" data-testid="back-to-trucks">
          <ArrowLeftIcon size={14} /> Back to fleet
        </Link>
        <Link to={`/ai?truck_id=${id}`}>
          <Button variant="outline" className="rounded-sm" data-testid="ai-truck-button">
            <ChatCircleDotsIcon size={16} weight="bold" className="mr-2" /> Ask Rep Fleet AI
          </Button>
        </Link>
        {user?.role === "admin" && (
          <Button variant="outline" onClick={del} className="rounded-sm border-red-300 text-red-700 hover:bg-red-50" data-testid="delete-truck">
            <TrashIcon size={16} weight="bold" className="mr-2" /> Delete
          </Button>
        )}
      </PageHeader>

      <div className="p-8 space-y-6">
        {/* Specs */}
        <div className="bg-white border border-slate-300 rounded-sm p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
            <Spec label="Status"><Badge tone={sb.tone}>{sb.label}</Badge></Spec>
            <Spec label={unitSystemFromTruck(truck) === "CA" ? "Mileage (km)" : "Mileage"} value={formatDistance(truck.mileage, unitSystemFromTruck(truck), 0)} mono />
            <Spec label="License plate" value={truck.license_plate || "—"} mono />
            <Spec label="Year" value={truck.year} mono />
            <Spec label="Driver" value={driver?.name || "Unassigned"} />
            <Spec label="Total repairs" value={`$${total_repair_cost.toLocaleString()}`} mono />
            {truck.fuel_percentage != null && (
              <Spec label="Fuel"><FuelGauge percentage={truck.fuel_percentage} size="sm" updatedAt={truck.fuel_updated_at} /></Spec>
            )}
            {truck.mpg_estimated != null && (
              <Spec label={unitSystemFromTruck(truck) === "CA" ? "Est. km/L" : "Est. MPG"} value={formatEconomy(truck.mpg_estimated, unitSystemFromTruck(truck)).replace(/ (MPG|km\/L)$/, "")} mono />
            )}
          </div>
          {truck.notes && (
            <div className="mt-6 pt-6 border-t border-slate-200">
              <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Notes</div>
              <p className="text-sm text-slate-700">{truck.notes}</p>
            </div>
          )}
        </div>

        {/* Live location card */}
        <div className="bg-white border border-slate-300 rounded-sm p-6" data-testid="location-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${truck.latitude != null ? "bg-blue-600" : "bg-slate-300"}`}>
                <MapPinIcon size={20} weight="bold" className="text-white" />
              </div>
              <div>
                <div className="section-eyebrow mb-0.5">/// Live tracking</div>
                <h3 className="font-display text-lg font-bold tracking-tight">Location</h3>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button variant="outline" onClick={() => setLinkOpen(true)} className="rounded-sm" data-testid="link-motive-button">
                <LinkIcon size={14} weight="bold" className="mr-2" />
                {truck.motive_vehicle_id ? "Re-link Motive" : "Link to Motive"}
              </Button>
              {truck.motive_vehicle_id && (
                <Button variant="outline" onClick={refreshLocation} disabled={refreshing || truck.location_hold} className="rounded-sm" data-testid="refresh-location-button">
                  <ArrowsClockwiseIcon size={14} weight="bold" className={`mr-2 ${refreshing ? "animate-spin" : ""}`} /> Refresh
                </Button>
              )}
              {truck.location_hold ? (
                <Button variant="outline" onClick={releaseLocation} className="rounded-sm border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="release-location-button">
                  <LockSimpleIcon size={14} weight="bold" className="mr-2" /> Release hold
                </Button>
              ) : (
                <Button variant="outline" onClick={() => { setHoldAddress(truck.location_address || ""); setHoldOpen(true); }} className="rounded-sm" data-testid="hold-location-button">
                  <MapPinLineIcon size={14} weight="bold" className="mr-2" /> Hold location
                </Button>
              )}
              <Button onClick={() => setNearbyOpen(true)} className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm" data-testid="find-nearby-service">
                <NavigationArrowIcon size={14} weight="bold" className="mr-2" /> Find nearby service
              </Button>
              <Button variant="outline" onClick={openShare} className="rounded-sm border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="share-track-button">
                <ShareNetworkIcon size={14} weight="bold" className="mr-2" /> Share link
              </Button>
            </div>
          </div>

          {truck.location_hold && (
            <div className="mt-4 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-sm px-4 py-3" data-testid="location-hold-banner">
              <LockSimpleIcon size={18} weight="fill" className="text-amber-700 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-900">
                <span className="font-bold">Location held.</span> This truck is pinned to <span className="font-semibold">{truck.location_address || "a fixed point"}</span> and Motive updates are paused
                {truck.location_held_at && <> since {new Date(truck.location_held_at).toLocaleString()}</>}. Click <span className="font-semibold">Release hold</span> to resume live tracking.
              </div>
            </div>
          )}

          {truck.latitude == null ? (
            <div className="mt-4 text-sm text-slate-600 border border-dashed border-slate-300 rounded-sm px-4 py-6 text-center">
              {truck.motive_vehicle_id
                ? "Linked to Motive — waiting for first location update. Click Refresh, or it will arrive within 5 minutes."
                : "Not linked to any Motive vehicle. Link this truck to enable live GPS tracking and nearby service search."}
            </div>
          ) : (
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-6">
              <Spec label="Coordinates" value={`${truck.latitude.toFixed(5)}, ${truck.longitude.toFixed(5)}`} mono />
              <Spec label="Address" value={truck.location_address || "—"} />
              <Spec label="Speed" value={truck.current_speed_mph != null ? `${Number(truck.current_speed_mph).toFixed(0)} mph` : "—"} mono />
              <Spec label="Last update" value={truck.location_updated_at ? new Date(truck.location_updated_at).toLocaleString() : "—"} mono />
            </div>
          )}
        </div>

        {/* Fuel telemetry card — only shown when Motive is linked */}
        {truck.motive_vehicle_id && (
          <div className="bg-white border border-slate-300 rounded-sm p-6" data-testid="fuel-card">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${truck.fuel_percentage != null ? "bg-emerald-600" : "bg-slate-300"}`}>
                  <GasPumpIcon size={20} weight="bold" className="text-white" />
                </div>
                <div>
                  <div className="section-eyebrow mb-0.5">/// Fuel telemetry</div>
                  <h3 className="font-display text-lg font-bold tracking-tight">Fuel level & efficiency</h3>
                </div>
              </div>
              {user?.role === "admin" && (
                <div className="flex items-center gap-2" data-testid="tank-editor">
                  {editingTank ? (
                    <>
                      <Input
                        type="number"
                        placeholder="Tank gallons"
                        value={tankVal}
                        onChange={(e) => setTankVal(e.target.value)}
                        className="h-9 w-32 rounded-sm font-mono text-sm"
                        data-testid="tank-input"
                      />
                      <span className="text-xs text-slate-500 font-mono">gal</span>
                      <Button size="sm" onClick={saveTank} className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="tank-save">
                        <CheckIcon size={14} weight="bold" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => { setEditingTank(false); setTankVal(truck.fuel_tank_capacity_gallons ?? ""); }} className="rounded-sm" data-testid="tank-cancel">
                        <XIcon size={14} weight="bold" />
                      </Button>
                    </>
                  ) : (
                    <Button variant="outline" size="sm" onClick={() => setEditingTank(true)} className="rounded-sm" data-testid="tank-edit">
                      <PencilSimpleIcon size={12} weight="bold" className="mr-1" />
                      Tank: {truck.fuel_tank_capacity_gallons ? `${truck.fuel_tank_capacity_gallons} gal` : "Set size"}
                    </Button>
                  )}
                </div>
              )}
            </div>

            {truck.fuel_percentage == null ? (
              <div className="mt-4 text-sm text-slate-600 border border-dashed border-slate-300 rounded-sm px-4 py-6 text-center">
                Waiting for first fuel reading from Motive. Some older ECMs or light gateways may not report fuel level.
              </div>
            ) : (
              <>
                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Current level</div>
                    <FuelGauge percentage={truck.fuel_percentage} size="lg" updatedAt={truck.fuel_updated_at} />
                  </div>
                  {truck.fuel_secondary_percentage != null && (
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Secondary tank</div>
                      <FuelGauge percentage={truck.fuel_secondary_percentage} size="lg" />
                    </div>
                  )}
                  <Spec label={unitSystemFromTruck(truck) === "CA" ? "Est. km/L" : "Est. MPG"} value={truck.mpg_estimated != null ? formatEconomy(truck.mpg_estimated, unitSystemFromTruck(truck)).replace(/ (MPG|km\/L)$/, "") : "—"} mono />
                  <Spec
                    label="Last fill-up"
                    value={truck.last_fill_up_at
                      ? `${truck.last_fill_up_gallons ? `${formatVolume(truck.last_fill_up_gallons, unitSystemFromTruck(truck), 1)} · ` : ""}${new Date(truck.last_fill_up_at).toLocaleDateString()}`
                      : "None yet"}
                    mono
                  />
                  <Spec label="Fuel updated" value={truck.fuel_updated_at ? new Date(truck.fuel_updated_at).toLocaleString() : "—"} mono />
                </div>

                {truck.motive_vehicle_id && (
                  <div className="mt-6" data-testid="fuel-chart">
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                      <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                        {chartRange === "day"
                          ? `Level on ${new Date(chartDate + "T00:00:00").toLocaleDateString()}`
                          : chartRange === "week"
                          ? `Level · 7 days ending ${new Date(chartDate + "T00:00:00").toLocaleDateString()}`
                          : "Level over last 30 days"}
                      </div>
                      <div className="flex items-center gap-2" data-testid="fuel-chart-filter">
                        <div className="flex items-center rounded-sm border border-slate-300 overflow-hidden bg-white">
                          {["day", "week", "month"].map((r) => (
                            <button
                              key={r}
                              type="button"
                              onClick={() => setChartRange(r)}
                              className={`px-3 py-1 text-[11px] font-mono uppercase tracking-wider ${chartRange === r ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"}`}
                              data-testid={`fuel-chart-range-${r}`}
                            >
                              {r}
                            </button>
                          ))}
                        </div>
                        {chartRange !== "month" && (() => {
                          const fmt = (d) => {
                            const yyyy = d.getFullYear();
                            const mm = String(d.getMonth() + 1).padStart(2, "0");
                            const dd = String(d.getDate()).padStart(2, "0");
                            return `${yyyy}-${mm}-${dd}`;
                          };
                          const today = new Date();
                          const min = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 30);
                          return (
                            <input
                              type="date"
                              value={chartDate}
                              max={fmt(today)}
                              min={fmt(min)}
                              onChange={(e) => setChartDate(e.target.value)}
                              className="h-7 px-2 text-[11px] font-mono border border-slate-300 rounded-sm bg-white"
                              data-testid="fuel-chart-date"
                            />
                          );
                        })()}
                      </div>
                    </div>
                    {fuelHistory.length > 1 ? (
                      <div style={{ width: "100%", height: 180 }}>
                        <ResponsiveContainer>
                          <LineChart data={fuelHistory.map((r) => {
                            const d = new Date(r.recorded_at);
                            let label;
                            if (chartRange === "day") {
                              label = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
                            } else if (chartRange === "week") {
                              label = d.toLocaleDateString([], { weekday: "short", day: "numeric" });
                            } else {
                              label = d.toLocaleDateString();
                            }
                            return { t: d.getTime(), label, pct: r.percentage };
                          })}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis dataKey="label" tick={{ fontSize: 10, fontFamily: "monospace" }} minTickGap={chartRange === "day" ? 40 : 20} />
                            <YAxis domain={[0, 100]} tick={{ fontSize: 10, fontFamily: "monospace" }} width={30} />
                            <Tooltip
                              formatter={(v) => [`${v}%`, "Fuel"]}
                              labelFormatter={(l) => l}
                              contentStyle={{ fontSize: 12, fontFamily: "monospace" }}
                            />
                            <ReferenceLine y={20} stroke="#f59e0b" strokeDasharray="4 4" label={{ value: "Low (20%)", position: "right", fontSize: 10, fill: "#f59e0b" }} />
                            <Line type="monotone" dataKey="pct" stroke="#059669" strokeWidth={2} dot={false} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <div className="h-[180px] flex items-center justify-center text-xs font-mono text-slate-400 border border-dashed border-slate-200 rounded-sm">
                        No fuel readings for this range yet.
                      </div>
                    )}
                  </div>
                )}

                {fuelEvents.length > 0 && (
                  <div className="mt-6" data-testid="fuel-events">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">Recent fill-ups (auto-detected)</div>
                    <table className="fleet-table text-xs">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Before → After</th>
                          <th>{unitSystemFromTruck(truck) === "CA" ? "Est. litres" : "Est. gallons"}</th>
                          <th>{unitSystemFromTruck(truck) === "CA" ? "Km since last" : "Miles since last"}</th>
                          <th>{unitSystemFromTruck(truck) === "CA" ? "km/L" : "MPG"}</th>
                          <th>Odometer</th>
                          <th>Receipt</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fuelEvents.map((e) => {
                          const truckRegion = unitSystemFromTruck(truck);
                          return (
                          <tr key={e.id} data-testid={`fuel-event-${e.id}`}>
                            <td className="font-mono">{new Date(e.recorded_at).toLocaleString()}</td>
                            <td className="font-mono">{e.before_pct?.toFixed(0)}% → {e.after_pct?.toFixed(0)}% <span className="text-emerald-600">(+{e.delta_pct?.toFixed(0)})</span></td>
                            <td className="font-mono tabular-nums">{e.estimated_gallons != null ? formatVolume(e.estimated_gallons, truckRegion, 1) : "—"}</td>
                            <td className="font-mono tabular-nums">{e.miles_since_last != null ? formatDistance(e.miles_since_last, truckRegion, 0) : "—"}</td>
                            <td className="font-mono tabular-nums font-bold">{e.mpg_since_last != null ? formatEconomy(e.mpg_since_last, truckRegion) : "—"}</td>
                            <td className="font-mono tabular-nums">{e.odometer_at_event ? formatDistance(e.odometer_at_event, truckRegion, 0) : "—"}</td>
                            <td>
                              {e.receipt_status === "uploaded" ? (
                                <span className="text-emerald-700 text-[11px] font-bold" title={e.receipt_notes || ""}>
                                  ✓ {(e.receipt_attachments || []).length} file{(e.receipt_attachments || []).length !== 1 ? "s" : ""}
                                </span>
                              ) : e.receipt_status === "dismissed" ? (
                                <span className="text-slate-400 text-[11px]">Dismissed</span>
                              ) : e.receipt_status === "no_driver" ? (
                                <span className="text-slate-400 text-[11px]">No driver</span>
                              ) : (
                                <span className="text-amber-700 text-[11px] font-bold">Awaiting driver</span>
                              )}
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {!truck.fuel_tank_capacity_gallons && (
                      <div className="mt-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1.5">
                        ⚠️ Set the tank capacity above to see estimated gallons and MPG on future fill-ups.
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        <Tabs defaultValue="repairs">
          <TabsList className="rounded-sm" data-testid="truck-tabs">
            <TabsTrigger value="repairs" data-testid="tab-repairs">
              <WrenchIcon size={14} weight="bold" className="mr-1.5" /> Repairs ({repairs.length})
            </TabsTrigger>
            <TabsTrigger value="services" data-testid="tab-services">
              <CalendarCheckIcon size={14} weight="bold" className="mr-1.5" /> Services ({services.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="repairs">
            <div className="bg-white border border-slate-300 rounded-sm">
              {repairs.length === 0 ? (
                <div className="p-8 text-center text-sm text-slate-500">No repair records yet.</div>
              ) : (
                <table className="fleet-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Type</th>
                      <th>Title</th>
                      <th>Shop / Mechanic</th>
                      <th>Mileage</th>
                      <th>Parts</th>
                      <th>Bill</th>
                      <th>Total</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {repairs.map((r) => {
                      const sb = statusBadge(r.status);
                      const isOutside = r.repair_type === "outside";
                      return (
                        <tr key={r.id} data-testid={`repair-row-${r.id}`}>
                          <td className="font-mono">{r.repair_date?.slice(0, 10)}</td>
                          <td><Badge tone={isOutside ? "warning" : "info"}>{isOutside ? "Outside" : "In-house"}</Badge></td>
                          <td className="font-semibold">{r.title}<div className="text-[10px] text-slate-500 capitalize font-mono">{r.category}</div></td>
                          <td className="text-xs">{r.mechanic || "—"}</td>
                          <td className="font-mono tabular-nums">{r.mileage_at_repair ? Number(r.mileage_at_repair).toLocaleString() : "—"}</td>
                          <td className="font-mono tabular-nums">${Number(r.parts_cost).toLocaleString()}</td>
                          <td className="font-mono tabular-nums">{isOutside ? `$${Number(r.shop_bill || 0).toLocaleString()}` : "—"}</td>
                          <td className="font-mono tabular-nums font-bold">${Number(r.total_cost).toLocaleString()}</td>
                          <td><Badge tone={sb.tone}>{sb.label}</Badge></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </TabsContent>

          <TabsContent value="services">
            <div className="bg-white border border-slate-300 rounded-sm">
              {services.length === 0 ? (
                <div className="p-8 text-center text-sm text-slate-500">No scheduled services yet.</div>
              ) : (
                <table className="fleet-table">
                  <thead>
                    <tr>
                      <th>Service</th>
                      <th>Last Date</th>
                      <th>Last Mileage</th>
                      <th>Next Due Date</th>
                      <th>Next Due Mileage</th>
                      <th>Cost</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {services.map((s) => {
                      const sb = statusBadge(s.status);
                      return (
                        <tr key={s.id} data-testid={`service-row-${s.id}`}>
                          <td className="capitalize font-semibold">{s.service_type.replace(/_/g, " ")}</td>
                          <td className="font-mono">{s.last_service_date?.slice(0, 10) || "—"}</td>
                          <td className="font-mono tabular-nums">{s.last_service_mileage ? Number(s.last_service_mileage).toLocaleString() : "—"}</td>
                          <td className="font-mono">{s.next_due_date?.slice(0, 10) || "—"}</td>
                          <td className="font-mono tabular-nums">{s.next_due_mileage ? Number(s.next_due_mileage).toLocaleString() : "—"}</td>
                          <td className="font-mono tabular-nums">${Number(s.cost || 0).toLocaleString()}</td>
                          <td><Badge tone={sb.tone}>{sb.label}</Badge></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <MotiveLinkDialog truck={truck} open={linkOpen} onOpenChange={setLinkOpen} onLinked={load} />
      <NearbyServiceDialog truck={truck} open={nearbyOpen} onOpenChange={setNearbyOpen} />

      <Dialog open={holdOpen} onOpenChange={setHoldOpen}>
        <DialogContent className="rounded-sm" data-testid="hold-location-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Hold truck at a parked location</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Drop a pin on the map <span className="font-semibold">or</span> type the address where <span className="font-semibold">Unit {truck.unit_number}</span> is parked. We'll pin it there and stop Motive from moving it (useful when Motive sends false-move readings) until you release the hold.
            </p>
            <div className="rounded-sm overflow-hidden border border-slate-200" style={{ height: 260 }}>
              <MapContainer
                center={truck.latitude != null ? [truck.latitude, truck.longitude] : [39.8283, -98.5795]}
                zoom={truck.latitude != null ? 11 : 4}
                style={{ height: "100%", width: "100%" }}
                scrollWheelZoom
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <ClickToPin point={pinPoint} onPick={onPickPin} />
              </MapContainer>
            </div>
            {pinPoint ? (
              <div className="text-xs font-mono text-emerald-700" data-testid="hold-pin-coords">
                📍 Pinned at {pinPoint.lat.toFixed(5)}, {pinPoint.lng.toFixed(5)} — {pinCity ? <span className="font-bold">{pinCity}</span> : "detecting city…"}. Click elsewhere to move it.
              </div>
            ) : (
              <div className="text-xs text-slate-400">Tip: click anywhere on the map to drop the parked pin (no API key needed).</div>
            )}
            {!pinPoint && (
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold text-slate-700 mb-1.5 block">Or type an address instead</Label>
                <Input
                  value={holdAddress}
                  onChange={(e) => setHoldAddress(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") holdLocation(); }}
                  placeholder="e.g. 1200 Truck Yard Rd, Brampton, ON"
                  className="rounded-sm"
                  data-testid="hold-address-input"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setHoldOpen(false); setPinPoint(null); setPinCity(""); }} className="rounded-sm" data-testid="hold-cancel">Cancel</Button>
            <Button onClick={holdLocation} disabled={holding} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="hold-confirm">
              {holding ? "Pinning…" : "Pin & hold"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="rounded-sm" data-testid="share-track-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Share live tracking link</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Anyone with this link can see <span className="font-semibold">Unit {truck.unit_number}</span>'s location on a map — no login needed. It always shows the real last-updated time, and expires automatically.
            </p>
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700 mb-1.5 block">Link valid for</Label>
              <div className="flex gap-2" data-testid="share-expiry-options">
                {[{ d: 1, l: "1 day" }, { d: 7, l: "7 days" }, { d: 30, l: "30 days" }].map((opt) => (
                  <button
                    key={opt.d}
                    type="button"
                    onClick={() => createShare(opt.d)}
                    disabled={creatingShare}
                    className={`px-3 py-1.5 border-2 rounded-sm text-xs font-bold transition-colors ${shareDays === opt.d ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}
                    data-testid={`share-expiry-${opt.d}`}
                  >{opt.l}</button>
                ))}
              </div>
            </div>
            {creatingShare ? (
              <div className="font-mono text-sm text-slate-500 py-4 text-center" data-testid="share-loading">Generating link…</div>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <Input value={shareUrl} readOnly className="rounded-sm font-mono text-xs" data-testid="share-url-input" onFocus={(e) => e.target.select()} />
                  <Button onClick={copyShare} className="bg-slate-900 hover:bg-slate-800 rounded-sm shrink-0" data-testid="share-copy-button">
                    <CopyIcon size={14} weight="bold" className="mr-2" /> {shareCopied ? "Copied" : "Copy"}
                  </Button>
                </div>
                {shareExpires && (
                  <div className="text-xs text-slate-500">Expires on {shareExpires.toLocaleString()}</div>
                )}
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={revokeShare} className="rounded-sm border-red-300 text-red-700 hover:bg-red-50" data-testid="share-revoke-button">Turn off link</Button>
            <Button variant="outline" onClick={() => setShareOpen(false)} className="rounded-sm" data-testid="share-done-button">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Spec({ label, value, children, mono }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">{label}</div>
      <div className={`text-sm font-semibold text-slate-900 ${mono ? "font-mono tabular-nums" : ""}`}>
        {children || value}
      </div>
    </div>
  );
}
